/**
 * Colour.
 *
 * The whole game is lit by one idea: the snake is the only real light source,
 * and everything else is the dark it moves through. Backgrounds stay cold and
 * desaturated so the emissive stuff — snake, motes, embers — can be the only
 * saturated thing on screen and read instantly.
 *
 * Each coil shifts the void's hue a few degrees so descending *feels* like
 * descending without ever changing the rules of what's readable.
 */

export interface Palette {
  voidTop: string
  voidBottom: string
  lattice: string
  latticeGlow: string
  dust: string
}

const COIL_PALETTES: Palette[] = [
  { voidTop: '#080b16', voidBottom: '#04050c', lattice: '#1b2440', latticeGlow: '#2f8fff', dust: '#5f7fbf' },
  { voidTop: '#0a0916', voidBottom: '#05040c', lattice: '#241d44', latticeGlow: '#7a4dff', dust: '#8a7fd0' },
  { voidTop: '#0d0814', voidBottom: '#06040a', lattice: '#2e1a3c', latticeGlow: '#c04dff', dust: '#b07fd0' },
  { voidTop: '#100810', voidBottom: '#080409', lattice: '#3a1a30', latticeGlow: '#ff4da6', dust: '#d07fae' },
  { voidTop: '#120a0a', voidBottom: '#090404', lattice: '#3f1e1e', latticeGlow: '#ff6a4d', dust: '#d0917f' },
  { voidTop: '#0f0d06', voidBottom: '#070603', lattice: '#3a3316', latticeGlow: '#ffb52e', dust: '#d0bb7f' },
  { voidTop: '#060f0b', voidBottom: '#030704', lattice: '#163a2a', latticeGlow: '#2effa6', dust: '#7fd0b0' },
  { voidTop: '#04080f', voidBottom: '#020407', lattice: '#122c3a', latticeGlow: '#2ed4ff', dust: '#7fbdd0' },
]

export function paletteFor(coil: number): Palette {
  return COIL_PALETTES[(coil - 1) % COIL_PALETTES.length]
}

/**
 * Snake colour by heat (0 = cold cyan, 1 = white-hot). Chain multiplier drives
 * this, so a player can read their own streak off the snake without looking at
 * the HUD — the number in the corner is confirmation, not information.
 */
const HEAT_STOPS: Array<[number, [number, number, number]]> = [
  [0.0, [64, 224, 255]],
  [0.35, [96, 255, 196]],
  [0.6, [255, 214, 92]],
  [0.82, [255, 138, 60]],
  // Incandescent, but still warm — a truly neutral white reads as "the colour
  // broke" rather than "this thing is about to burn through the floor".
  [1.0, [255, 244, 214]],
]

export function heatColor(t: number): [number, number, number] {
  const x = clamp01(t)
  for (let i = 1; i < HEAT_STOPS.length; i++) {
    const [p1, c1] = HEAT_STOPS[i - 1]
    const [p2, c2] = HEAT_STOPS[i]
    if (x <= p2) {
      const k = (x - p1) / (p2 - p1 || 1)
      return [lerp(c1[0], c2[0], k), lerp(c1[1], c2[1], k), lerp(c1[2], c2[2], k)]
    }
  }
  return HEAT_STOPS[HEAT_STOPS.length - 1][1]
}

export const COLORS = {
  mote: '#5ef0ff',
  moteCore: '#eaffff',
  ember: '#ffc24d',
  emberCore: '#fff3d0',
  prism: '#e05cff',
  prismCore: '#ffe8ff',
  bloom: '#b53cff',
  bloomOpen: '#ff3c8a',
  danger: '#ff3b5c',
  light: '#7dffd4',
  lightLow: '#ff9040',
} as const

export function rgba(c: [number, number, number], a: number): string {
  return `rgba(${Math.round(c[0])},${Math.round(c[1])},${Math.round(c[2])},${a})`
}

export function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v
}

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t
}

/** Colour-blind safe alternative for the two colours that carry meaning. */
export function applyDaltonism(on: boolean): void {
  document.documentElement.classList.toggle('daltonize', on)
}
